import React from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { apiClient } from '../../../helpers/apiHelper';
import { Button } from '../../ComponnentsUI/common/Button';
import { FormField } from '../../ComponnentsUI/common/FormField';
import { useState } from 'react';
import { Input } from '../../ComponnentsUI/common/Input';
import { IoFlash } from 'react-icons/io5';
import smallLogo from '../../../home-icon-blue.png';
import darksmallLogo from '../../../home-icon-white.png';
import { useDocumentTitle } from '../../../hooks/useDocumentTitle';
import { useTranslation } from 'react-i18next';

const ForgotPassword = () => {
  const { t } = useTranslation('ForgotPassword');
  
  // State management for the form input, replacing Ant Design's form hook
  const [email, setEmail] = useState('');

  // Existing state for messages
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // useMutation hook logic is preserved, but onSuccess is updated to reset our manual state
  const { mutate, isPending } = useMutation({
    mutationFn: (data: { email: string }) =>
      apiClient.post('/auth/forgot-password', data),
    onSuccess: () => {
      setErrorMessage('');
      setSuccessMessage(t('successMessage'));
      setEmail(''); // Reset the input field manually
    },
    onError: (error: any) => {
      setSuccessMessage('');
      setErrorMessage(
        error.response?.data?.detail || t('errors.unexpectedError')
      );
    },
  });

  // handleSubmit now uses a standard form event and reads from React state
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email) {
      setErrorMessage(t('errors.emailRequired'));
      return;
    }
    mutate({ email });
  };

  useDocumentTitle(`${t('title')} - ${t('pageTitle')}`);

  return (
    // Main container with a subtle light gradient background, matching the login page
    <div className="min-h-screen w-full flex justify-center items-center p-4 bg-card-color-200 dark:bg-gray-900">
      <div className="w-full max-w-md bg-white p-8 sm:p-10 rounded-2xl shadow-lg bg-card-color-100 dark:bg-gray-800">
        {/* Consistent Logo and App Name Section */}
        <div className="flex items-center justify-center mb-6 space-x-3">
          <img
            src={smallLogo}
            className={`dark:hidden transition-all duration-300 h-10`}
            alt="Deep Echo Logo"
          />
          <img
            src={darksmallLogo}
            className="hidden dark:block transition-all duration-300 h-10"
            alt="Deep Echo Logo"
          />
          <div>
            <h1 className="text-xl font-bold text-title-page-color-100">{t('title')}</h1>
            <p className="text-xs text-gray-500">{t('subtitle')}</p>
          </div>
        </div>

        {/* Conditional rendering for the success message or the form */}
        {successMessage ? (
          <div className="text-center">
            <h2 className="text-2xl font-bold text-title-page-color-100">
              {t('checkEmailTitle')}
            </h2>
            <p className="p-4 mt-4 text-sm text-green-800 bg-green-50 rounded-lg">
              {successMessage}
            </p>
            <Link to="/login">
              <Button variant="primary" size="lg" className="w-full mt-6">
                {t('backToSignIn')}
              </Button>
            </Link>
          </div>
        ) : (
          <>
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-title-page-color-100">
                {t('pageTitle')}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {t('pageSubtitle')}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <FormField label={t('emailLabel')} htmlFor="email">
                <Input
                  id="email"
                  type="email"
                  placeholder={t('emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-gray-50 border-gray-200 focus:bg-white"
                  required
                />
              </FormField>

              {errorMessage && (
                <p className="text-red-500 text-sm text-center font-medium">
                  {errorMessage}
                </p>
              )}

              <div className="pt-2">
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  className="w-full"
                  disabled={isPending}
                >
                  {isPending ? t('sendingLink') : t('sendResetLink')}
                </Button>
              </div>
            </form>

            <p className="text-sm text-center text-gray-500 mt-6">
              {t('rememberPassword')}{' '}
              <Link
                to="/login"
                className="font-medium text-blue-600 hover:text-blue-500 transition-colors"
              >
                {t('backToSignIn')}
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;
